import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

interface AuthContextType {
  isAuthenticated: boolean;
  user: any;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    
    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('🔄 AuthContext: Starting session check...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (cancelled) return;
        
        if (error) {
          console.error('❌ AuthContext: Error getting session:', error);
          console.error('❌ Session error details:', {
            message: error.message,
            status: error.status,
            name: error.name
          });
          
          // Check if it's a network error
          if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
            setAuthError('network');
            console.warn('🌐 AuthContext: Network error detected, setting offline mode');
          } else {
            setAuthError('auth');
          }
        } else if (session) {
          console.log('✅ AuthContext: Session found, user authenticated');
          setIsAuthenticated(true);
          setUser(session.user);
          setAuthError(null);
        } else {
          console.log('ℹ️ AuthContext: No session found, user not authenticated');
          setAuthError(null);
        }
      } catch (error) {
        console.error('❌ AuthContext: Session check failed:', error);
        console.error('❌ Critical session error:', {
          message: error.message,
          stack: error.stack
        });
        
        if (!cancelled) {
          setAuthError('network');
          console.warn('🌐 AuthContext: Setting offline mode due to session check failure');
        }
      } finally {
        if (!cancelled) {
          console.log('✅ AuthContext: Setting loading to false');
          setLoading(false);
        }
      }
    };

    getInitialSession();
    
    return () => {
      cancelled = true;
    };
  }, []);
  
  useEffect(() => {
    let cancelled = false;
    let hydrated = false; // Track if we've processed initial session

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (cancelled) return;

        console.log('🔄 AuthContext: Auth state changed:', event, session?.user?.email);

        // ✅ Debounce INITIAL_SESSION and SIGNED_IN to prevent duplicate fetches
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
          // Skip duplicate INITIAL_SESSION events
          if (hydrated && event === 'INITIAL_SESSION') {
            console.log('⏭️ AuthContext: Skipping duplicate INITIAL_SESSION');
            return;
          }

          if (session) {
            console.log('✅ AuthContext: User signed in successfully');
            setIsAuthenticated(true);
            setUser(session.user);
            setAuthError(null);
            hydrated = true;

            // Check if this is from email confirmation (not from home page)
            if (location.pathname.includes('confirm') || location.pathname.includes('verified')) {
              toast.success('Email confirmed successfully! Welcome to BOTINO.NET');
              navigate('/dashboard');
            }
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('ℹ️ AuthContext: User signed out');
          setIsAuthenticated(false);
          setUser(null);
          setAuthError(null);
          hydrated = false;
          // Remove any legacy tokens that might exist
          localStorage.removeItem('token');
        }

        if (!cancelled) {
          console.log('✅ AuthContext: Auth state change processed, setting loading to false');
          setLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [location, navigate]);

  // Redirect logic
  useEffect(() => {
    if (!loading) {
      const protectedRoutes = ['/dashboard', '/admin', '/my-bots', '/create-bot', '/active-bots'];
      const isProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));
      
      if (!isAuthenticated && isProtectedRoute) {
        navigate('/signin');
      }
    }
  }, [isAuthenticated, location.pathname, navigate, loading]);

  const login = async (email: string, password: string) => {
    try {
      console.log('Attempting login with Supabase:', { email });
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('Supabase login error:', error);
        throw new Error(error.message);
      }
      
      if (!data.session?.access_token) {
        throw new Error('No authentication session received');
      }
      
      // Check if admin user
      if (email === 'admin@botino.net') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }

      toast.success('Successfully logged in');
    } catch (error: any) {
      console.error('Login error:', error);
      
      let errorMessage = 'Login failed';
      
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password';
      } else if (error.message.includes('Too many requests')) {
        errorMessage = 'Too many login attempts. Please try again later.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      console.log('Attempting registration with Supabase:', { email, name });
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name,
            full_name: name
          },
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });
      
      if (error) {
        console.error('Supabase registration error:', error);
        throw new Error(error.message);
      }
      
      console.log('Registration response:', data);
      
      // Check if user was auto-logged in (email confirmation disabled)
      if (data.session) {
        console.log('User was auto-logged in - email confirmation is disabled');
        toast.success('Account created successfully! Welcome to BOTINO.NET');
        navigate('/dashboard');
        return;
      }
      
      // If no session but user exists, email confirmation is required
      if (data.user && !data.session) {
        console.log('User needs to confirm email');
        return; // Let the UI show the confirmation message
      }

    } catch (error: any) {
      console.error('Registration error:', error);
      
      let errorMessage = 'Registration failed';
      
      if (error.message.includes('User already registered')) {
        errorMessage = 'An account with this email already exists';
      } else if (error.message.includes('Password should be')) {
        errorMessage = 'Password must be at least 6 characters long';
      } else if (error.message.includes('Invalid email')) {
        errorMessage = 'Please enter a valid email address';
      } else if (error.message.includes('Signup is disabled')) {
        errorMessage = 'Registration is currently disabled. Please contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      // Auth state listener will handle cleanup
      navigate('/');
      toast.success('Successfully logged out');
    } catch (error) {
      console.error('Logout error:', error);
      // Force cleanup even if signOut fails
      setIsAuthenticated(false);
      setUser(null);
      // Only remove legacy tokens, let Supabase handle its own session storage
      localStorage.removeItem('token');
      navigate('/');
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};